import { fetchProducts } from "../../utilities/fetchProducts";
import { getTranslation } from "./translations";

function capitaliseFirst(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// Helper: Update intro text with the user's name
function updateIntroName(name) {
  if (!name) return;
  const capitalisedName = capitaliseFirst(name);
  const copyEl = document.querySelector('.replenishment-intro__copy');
  if (copyEl) {
    const temp = document.createElement('div');
    temp.innerHTML = copyEl.innerHTML.trim();
    const strongEl = temp.querySelector('strong');
    if (strongEl) {
      strongEl.innerHTML = strongEl.innerHTML.replace(/^Hey,?/i, `Hey ${capitalisedName},`);
      copyEl.innerHTML = temp.innerHTML;
    }
  }
}

// Helper: Get products from URL query string
function getProductsFromURL(search) {
  const params = new URLSearchParams(search.substring(1));
  const name = params.get('name');
  let capitalisedName = "";
  if (name) {
    capitalisedName = capitaliseFirst(name);
    updateIntroName(name);
    params.delete('name');
  }
  const products = [];
  for (const [key] of params.entries()) {
    const [productId, quantity] = key.split('-');
    if (productId && quantity) {
      products.push({
        productId,
        quantity: parseInt(quantity, 10),
      });
    }
  }
  return { products, name: capitalisedName };
}

// Helper: Get products from DOM elements
function getProductsFromDOM(selector = '.account-product') {
  const dataEls = document.querySelectorAll(selector);
  const products = [];
  dataEls.forEach(el => {
    const productId = el.getAttribute('data-product-id');
    const quantity = parseInt(el.getAttribute('data-quantity'), 10);
    if (productId && quantity) {
      products.push({ productId, quantity });
    }
  });

  return products;
}

// Check if current page is the replenish page
// (?view=replenishment covers template previews on other page handles)
function isReplenishPage() {
  return window.location.pathname.includes('/replenish')
    || new URLSearchParams(window.location.search).get('view') === 'replenishment';
}

// Main logic
let products = [];
let name = "";
if (isReplenishPage()) {
  // Get from URL, update intro copy with name
  const result = getProductsFromURL(window.location.search);
  products = result.products;
  name = result.name; // (capitalized)
} else {
  // Get from DOM elements
  products = getProductsFromDOM();
}

const productIds = products.map(p => p.productId);

const deliveryBanner = document.querySelector('.replenishment-products__shipping-banner');

// Free shipping config — window.cartData is the source of truth (published by
// the inline cart from snippets/free-shipping-config.liquid, per-country, in
// the presentment currency — same contract as the UK theme). The section
// settings are only a fallback, so this page can't drift from the cart bar.
// parseAmount strips "$" so a "$9.99"-style value still parses.
function parseAmount(value) {
  const n = parseFloat(String(value ?? '').replace(/[^0-9.]/g, ''));
  return isNaN(n) ? 0 : n;
}
const cartPossible = window.cartData?.freeShippingPossible === 'true';
const cartThreshold = parseAmount(window.cartData?.freeShippingThreshold);
const freeShippingThreshold = cartPossible && cartThreshold > 0
  ? cartThreshold
  : parseAmount(deliveryBanner?.getAttribute('data-free-ship'));
const baseShippingPrice = parseAmount(window.cartData?.baseShippingPrice) > 0
  ? parseAmount(window.cartData?.baseShippingPrice)
  : parseAmount(deliveryBanner?.getAttribute('data-base-shipping'));
const freeShippingPossible = freeShippingThreshold > 0;
const subTotalPriceElement = document.querySelector('.replenishment-products__reorder__subtotal');
const savingsElement = document.querySelector('.replenishment-products__reorder__savings');
const freeShippingElement = document.querySelector('.replenishment-products__reorder__shipping');
const discountElement = document.querySelector('.replenishment-products__reorder__discount');
const totalPriceElement = document.querySelector('.replenishment-products__reorder__total');

document.querySelectorAll('.reorderButton')
  .forEach(button => button.addEventListener('click', addToCart));

fetchProducts(productIds)
  .then(async fetchedProducts => {
    // Match each fetched product to its quantity
    const enriched = await Promise.all(
      fetchedProducts.map(async fp => {
        const original = products.find(p => p.productId === fp.id);
        // Fetch product data from Shopify using handle
        const handle = fp.handle;
        const shopifyRes = await fetch(`/products/${handle}.js`);
        const shopifyProduct = await shopifyRes.json();

        // Get price from selling_plan_allocations in variants
        let sellingPlanPrice = null;
        if (shopifyProduct.variants && shopifyProduct.variants.length > 0) {
          // Find the first variant with selling_plan_allocations
          for (const variant of shopifyProduct.variants) {
            if (
              variant.selling_plan_allocations &&
              variant.selling_plan_allocations.length > 0
            ) {
              // Get the price from the first selling_plan_allocation
              sellingPlanPrice = variant.selling_plan_allocations[0].price / 100;
              break;
            }
          }
        }

        return {
          ...fp,
          quantity: original?.quantity || 1,
          sellingPlanPrice,
        };
      })
    );

    updateDOMWithProducts(enriched);
  })
  .catch(err => {
    console.error('Error fetching products:', err);
  });

// Reviews.io rating-snippet init — the rows render async, so the loader's own
// DOMContentLoaded scan misses them. Retry briefly in case dist.js is still loading.
function initRatingSnippets(attempt = 0) {
  if (typeof window.ratingSnippet === 'function') {
    window.ratingSnippet('ruk_rating_snippet', {
      store: 'novomins.com.au',
      mode: 'default',
      color: '#1D2025',
      linebreak: false,
      text: 'reviews',
      singularText: 'review',
      lang: 'en',
      usePolaris: true,
      showEmptyStars: false,
      showNumericalAverageRating: false
    });
  } else if (attempt < 20) {
    setTimeout(() => initRatingSnippets(attempt + 1), 250);
  }
}

function updateDOMWithProducts(products) {
  const container = document.querySelector('#productsList');

  let subscribeButtonText = getTranslation('subscribeButton');

  products.forEach(product => {
    let savingsPercentage = '';

    if ((product.compareAtPrice > 0) && product.sellingPlanPrice > 0) {
      savingsPercentage = (((product.compareAtPrice - product.sellingPlanPrice) / product.compareAtPrice) * 100).toFixed(0) + '%';
    } else if (product.sellingPlanPrice > 0) {
      savingsPercentage = (((product.price - product.sellingPlanPrice) / product.price) * 100).toFixed(0) + '%';
    }

    const item = document.createElement('div');
    item.className = 'replenishment-product';
    item.setAttribute('data-varaint-id', product.variantId);
    item.setAttribute('data-price', product.price);
    item.setAttribute('data-quantity', product.quantity);
    item.setAttribute('data-selling-plan-id', product.sellingPlanId || 'null');
    item.setAttribute('data-selected', 'true');
    item.setAttribute('data-subscription', 'false');
    item.setAttribute('data-subscription-price', product.sellingPlanPrice || 'null');
    item.setAttribute('data-compare-at-price', product.compareAtPrice || '0');

    item.innerHTML = `
      <div class="replenishment-product__overlay">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="#ccc" stroke-width="4" opacity="0.25" />
          <path d="M22 12a10 10 0 0 1-10 10" stroke="#892498" stroke-width="4" stroke-linecap="round">
            <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite" />
          </path>
        </svg>
      </div>

      <div class="replenishment-product__media">
        <img src="${product.imgUrl}" alt="${product.imgAltText}" />
      </div>

      <div class="replenishment-product__details">
        <div class="ruk_rating_snippet" data-sku="${product.sku || ''}"></div>
        <h3>${product.title}</h3>
        ${product.supportingTagLine && product.supportingTagLine !== 'null' ? `
          <p class="replenishment-product__details__tagline">${product.supportingTagLine}</p>
        ` : ''}
      </div>

      <div class="replenishment-product__price">
        ${product.compareAtPrice > product.price ? `
          <s>${formatMoney(Number(product.compareAtPrice) * 100)}</s>
        ` : ''}
        <span class="replenishment-product__price__value">${formatMoney(Number(product.price) * 100)}</span>
      </div>

      ${product.sellingPlanPrice !== null ? `
        <div class="replenishment-product__sub-price">
          ${formatMoney(Number(product.sellingPlanPrice) * 100)}
        </div>

        <div class="replenishment-product__sub-price--selected">
          <span class="sub-price-value">${formatMoney(Number(product.sellingPlanPrice) * 100)}</span>
          <span class="savings-text">${getTranslation('savingText')} ${savingsPercentage} ${getTranslation('postPercentageText')}</span>
          <div class="delivery-info">
            <svg xmlns="http://www.w3.org/2000/svg" width="29" height="26" viewBox="0 0 29 26" fill="none">
              <path d="M10.9403 21.0447C12.6462 22.3101 14.7605 23.0589 17.0502 23.0589C22.7087 23.0589 27.2958 18.4859 27.2958 12.8447C27.2958 7.20346 22.7087 2.63037 17.0502 2.63037C11.7765 2.63037 7.43348 6.60265 6.86719 11.7097" stroke="#1D2025" stroke-width="1.3" stroke-linecap="round"/>
              <path d="M16.1523 6.34497V13.7735H20.7952" stroke="#1D2025" stroke-width="1.3" stroke-linecap="round"/>
              <path d="M10.5831 11.9167H5.01172" stroke="#1D2025" stroke-width="1.3" stroke-linecap="round"/>
              <path d="M7.79297 14.7019H1.29297" stroke="#1D2025" stroke-width="1.3" stroke-linecap="round"/>
              <path d="M11.5117 18.4167H5.01172" stroke="#1D2025" stroke-width="1.3" stroke-linecap="round"/>
            </svg>
            ${getTranslation('deliveryEvery')}
          </div>
        </div>
        ` : `
        <div class="replenishment-product__sub-price replenishment-product__sub-price--no-plan">
          N/A
        </div>
        `}

      <div class="replenishment-product__checkout">
        <div>
          <div class="replenishment-product__qty">
            <button class="qty-minus" aria-label="Decrease quantity">
              <svg xmlns="http://www.w3.org/2000/svg" width="9" height="2" viewBox="0 0 9 2" fill="none">
                <path d="M8.10156 1L0.601562 1" stroke="#1D2025"/>
              </svg>
            </button>
            <span class="displayed-quantity">${product.quantity}</span>
            <button class="qty-plus" aria-label="Increase quantity">
              <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none">
                <path d="M3.85156 0.25V7.75" stroke="#1D2025"/>
                <path d="M7.60156 4L0.101562 4" stroke="#1D2025"/>
              </svg>
            </button>
          </div>

          <div class="replenishment-product__total">
            ${formatMoney((Number(product.price) * product.quantity) * 100)}
          </div>

          <label class="custom-checkbox">
            <input type="checkbox" checked />
            <span class="checkmark"></span>
          </label>
        </div>

        ${product.sellingPlanPrice !== null ? `
          <div class="replenishment-product__upgrade">${subscribeButtonText}</div>
        ` : ``}
      </div>
    `;

    container.appendChild(item);
  });

  updateTotalPrice();
  initRatingSnippets();

  // Attach event listeners for quantity buttons
  const minusButtons = document.querySelectorAll('.qty-minus');
  const plusButtons = document.querySelectorAll('.qty-plus');

  minusButtons.forEach(button => {
    button.addEventListener('click', () => changeQuanity(button, 'minus'));
  });

  plusButtons.forEach(button => {
    button.addEventListener('click', () => changeQuanity(button, 'plus'));
  });

  // Add event listener for the custom checkbox
  const checkboxes = document.querySelectorAll('.custom-checkbox input[type="checkbox"]');
  checkboxes.forEach(checkbox => {
    checkbox.addEventListener('change', (event) => {
      const product = event.target.closest('.replenishment-product');
      if (product) {
        product.setAttribute('data-selected', event.target.checked ? 'true' : 'false');
        updateTotalPrice();
      }
    });
  });

  // Event listener for the upgrade functionality
  const upgradeButtons = document.querySelectorAll('.replenishment-product__upgrade');
  upgradeButtons.forEach(button => {
    button.addEventListener('click', (event) => {
      const product = event.target.closest('.replenishment-product');

      const currentSubscription = product.getAttribute('data-subscription');
      const newSubscription = currentSubscription === 'true' ? 'false' : 'true';
      product.setAttribute('data-subscription', newSubscription);

      // Update the clicked button
      if (newSubscription === 'true') {
        button.textContent = getTranslation('oneTimePurchaseButton');
        button.classList.add('active');
      } else {
        button.textContent = subscribeButtonText;
        button.classList.remove('active');
      }

      const quantity = parseInt(product.getAttribute('data-quantity'));
      const subscriptionPrice = parseFloat(product.getAttribute('data-subscription-price'));
      const regularPrice = parseFloat(product.getAttribute('data-price'));

      const unitPrice = newSubscription === 'true' && !isNaN(subscriptionPrice)
        ? subscriptionPrice
        : regularPrice;

      const totalEl = product.querySelector('.replenishment-product__total');
      if (totalEl) {
        totalEl.textContent = formatMoney(unitPrice * quantity * 100);
      }

      updateTotalPrice();
    });
  });
}

// Free shipping progress bar (below-threshold prompt + fill).
// Text templates come from the section settings via data attributes.
function updateShippingProgress(totalPrice) {
  if (!deliveryBanner) return;

  if (!freeShippingPossible) {
    deliveryBanner.style.display = 'none';
    return;
  }

  const msgEl = deliveryBanner.querySelector('[data-shipping-msg]');
  const maxEl = deliveryBanner.querySelector('[data-shipping-threshold]');
  const barEl = deliveryBanner.querySelector('[data-shipping-progress]');

  const remaining = freeShippingThreshold - totalPrice;
  // The $40 threshold applies to subscription lines too (since 2026-10-09).
  const progress = Math.max(0, Math.min(100, (totalPrice / freeShippingThreshold) * 100));

  if (remaining > 0) {
    const template = deliveryBanner.getAttribute('data-progress-text') || 'Spend [amount] more for free shipping';
    msgEl.innerHTML = template.replace('[amount]', `<strong>${formatMoney(remaining * 100)}</strong>`);
  } else {
    msgEl.innerHTML = deliveryBanner.getAttribute('data-unlocked-text') || "You've unlocked FREE shipping!";
  }

  // "$50" — trim trailing zero cents for the cap label
  maxEl.textContent = formatMoney(freeShippingThreshold * 100).replace(/[.,]00(?=\D*$|$)/, '');
  barEl.style.width = `${progress}%`;

  deliveryBanner.style.display = 'flex';
}

function updateTotalPrice() {
  const products = document.querySelectorAll('.replenishment-product');
  let totalPrice = 0;
  let subTotalPrice = 0;
  let selectedCount = 0;

  products.forEach(product => {
    // Check if product is selected
    if (product.getAttribute('data-selected') !== 'true') return;

    const quantity = parseInt(product.getAttribute('data-quantity'), 10);
    const productCompareAtPrice = parseFloat(product.getAttribute('data-compare-at-price'));
    const isSubscription = product.getAttribute('data-subscription') === 'true';
    const productPrice = isSubscription ? parseFloat(product.getAttribute('data-subscription-price')) : parseFloat(product.getAttribute('data-price'));

    selectedCount += 1;

    totalPrice += productPrice * quantity;

    if (productCompareAtPrice > productPrice) {
      subTotalPrice += productCompareAtPrice * quantity;
    } else {
      subTotalPrice += productPrice * quantity;
    }
  });

  updateShippingProgress(totalPrice);

  const freeShippingEarned = freeShippingPossible && baseShippingPrice > 0
    && totalPrice >= freeShippingThreshold;

  if (freeShippingEarned) {
    subTotalPrice += baseShippingPrice;
  }

  const productSavings = subTotalPrice - totalPrice - (freeShippingEarned ? baseShippingPrice : 0);
  const totalDiscount = subTotalPrice - totalPrice;

  if (productSavings > 0 && savingsElement) {
    savingsElement.style.display = 'flex';
    savingsElement.querySelector('span').textContent = `-${formatMoney(productSavings * 100)}`;
  } else {
    savingsElement.style.display = 'none';
  }

  if (freeShippingEarned) {
    freeShippingElement.style.display = 'flex';
    freeShippingElement.querySelector('span').innerHTML = `-${formatMoney(baseShippingPrice * 100)}<em>FREE</em>`;
  } else {
    freeShippingElement.style.display = 'none';
  }

  if (totalDiscount > 0) {
    discountElement.style.display = 'flex';
    discountElement.querySelector('span').textContent = `-${formatMoney(totalDiscount * 100)}`;
  } else {
    discountElement.style.display = 'none';
  }

  if ((subTotalPrice > totalPrice) && subTotalPriceElement) {
    subTotalPriceElement.style.display = 'flex';
    subTotalPriceElement.querySelector('span').textContent = formatMoney(subTotalPrice * 100);
  }

  if (subTotalPrice > totalPrice) {
    totalPriceElement.querySelector('span').innerHTML = `${formatMoney(totalPrice * 100)} <s>${formatMoney(subTotalPrice * 100)}</s>`;
  } else {
    totalPriceElement.querySelector('span').textContent = formatMoney(totalPrice * 100);
  }

  // "Reorder · $70.97" on both buttons
  document.querySelectorAll('.reorderButton .reorder-amount').forEach(el => {
    el.textContent = `· ${formatMoney(totalPrice * 100)}`;
  });
}


function changeQuanity(el, action) {
  const product = el.closest('.replenishment-product');
  const overlay = product.querySelector('.replenishment-product__overlay');
  const quantityEl = product.querySelector('.displayed-quantity');
  let quantity = parseInt(quantityEl.textContent, 10);

  overlay.style.display = 'flex';
  overlay.style.pointerEvents = 'none'; // Disable pointer events during update

  if (action === 'plus') {
    quantity += 1;
  } else if (action === 'minus' && quantity > 1) {
    // Prevent going below 1
    if (quantity === 1) {
      overlay.style.display = 'none'; // Hide overlay if quantity is 1
      return;
    }
    quantity -= 1;
  }

  product.setAttribute('data-quantity', quantity);
  quantityEl.textContent = quantity;

  // Update total price for this product
  const isSubscription = product.getAttribute('data-subscription') === 'true';
  const price = isSubscription ? parseFloat(product.getAttribute('data-subscription-price')) : parseFloat(product.getAttribute('data-price'));
  const totalEl = product.querySelector('.replenishment-product__total');
  totalEl.textContent = formatMoney(price * quantity * 100);

  overlay.style.display = 'none';

  updateTotalPrice();
}


async function addToCart() {
  const inlineCart = document.querySelector('.inline-cart');
  const inlineCartPageOverlay = document.querySelector('.inline-cart__page-overlay');
  const products = document.querySelectorAll('.replenishment-product');
  const items = [];

  products.forEach((product) => {
    if (product.getAttribute('data-selected') !== 'true') return;

    const productObj = {};

    productObj.id = product.getAttribute('data-varaint-id');
    productObj.quantity = product.getAttribute('data-quantity');

    if (product.getAttribute('data-subscription') === 'true') {
      productObj.selling_plan = product.getAttribute('data-selling-plan-id');
    }

    items.push(productObj);
  })

  await fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify({ items })
  });

  inlineCart.classList.add('active');
  inlineCartPageOverlay.classList.add('active');
}
